import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Chip, Button, TextField, CircularProgress,
  ToggleButtonGroup, ToggleButton, Collapse, IconButton, Divider, useTheme,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import {
  ThumbUpAltOutlined, ThumbDownAltOutlined, CheckCircleOutline,
  ExpandMore, ExpandLess, InsightsOutlined, LibraryAddOutlined,
  HighlightOffOutlined,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import http from '../../services/http';

// Card de KPI (dashboard de qualidade do Orby).
const StatCard = ({ label, value, accent, icon }) => {
  const theme = useTheme();
  return (
    <Paper
      elevation={0}
      sx={{
        flex: '1 1 160px',
        minWidth: 150,
        p: 2,
        borderRadius: 3,
        border: `1px solid ${theme.palette.divider}`,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        background: theme.palette.background.paper,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: accent || theme.palette.text.secondary }}>
        {icon}
        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 600, letterSpacing: 0.3 }}>
          {label}
        </Typography>
      </Box>
      <Typography variant="h4" sx={{ fontWeight: 700, color: accent || theme.palette.text.primary, lineHeight: 1.1 }}>
        {value}
      </Typography>
    </Paper>
  );
};

const FeedbackItem = ({ item, onReviewed }) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [correcting, setCorrecting] = useState(false);
  const [docTitle, setDocTitle] = useState('');
  const [docContent, setDocContent] = useState('');
  const [savingDoc, setSavingDoc] = useState(false);
  const isPending = item.status === 'pending';
  const ehCurtida = item.rating === 'like';

  // A curtida RECUSADA: o revisor disse que a resposta nao era boa, e isso
  // nao encerra o assunto -- ela fica aqui como caso a corrigir na base, do
  // mesmo jeito que uma reclamacao pendente. So depois de corrigida e que
  // vira revisada.
  const foiRecusada = item.status === 'rejected';
  const precisaDeCorrecao = isPending || foiRecusada;

  const review = async (outcome) => {
    setSaving(true);
    try {
      await http.put(`/ai-feedback/${item.id}/review`, {
        review_notes: notes.trim() || undefined,
        ...(outcome ? { outcome } : {}),
      });
      /*
       * O mesmo `rejected` quer dizer coisas diferentes nos dois lados, e por
       * isso o aviso muda:
       *
       * - na CURTIDA, recusar é dizer "a resposta não era boa": o caso fica,
       *   como correção pendente;
       * - na RECLAMAÇÃO, descartar é dizer "não há o que corrigir": o caso
       *   sai da fila, que é o pedido da Gisele (18/10).
       */
      toast.success(
        outcome === 'rejected'
          ? (ehCurtida ? 'Curtida recusada: vai para correção.' : 'Reclamação descartada.')
          : 'Marcado como revisado.'
      );
      onReviewed(item.id, outcome, ehCurtida);
    } catch (e) {
      toast.error('Não foi possível salvar a revisão.');
    } finally {
      setSaving(false);
    }
  };

  const openCorrection = () => {
    const q = (item.question || '').trim();
    setDocTitle(q ? q.slice(0, 120) : 'Correção do Orby');
    setDocContent('');
    setCorrecting(true);
  };

  const saveCorrection = async () => {
    const title = docTitle.trim();
    const content = docContent.trim();
    if (!title || !content) {
      toast.warn('Preencha o título e o conteúdo da correção.');
      return;
    }
    setSavingDoc(true);
    try {
      const res = await http.post('/ai-documents', {
        title,
        content,
        category: 'Correções do Orby',
        tags: ['orby', 'correção'],
      });
      const docId = res.data?.document?.id;
      await http.put(`/ai-feedback/${item.id}/review`, {
        review_notes: docId ? `Corrigido na base de conhecimento (documento #${docId}).` : 'Corrigido na base de conhecimento.',
      });
      toast.success('Correção adicionada à base e feedback revisado.');
      setCorrecting(false);
      onReviewed(item.id);
    } catch (e) {
      toast.error('Não foi possível salvar a correção na base.');
    } finally {
      setSavingDoc(false);
    }
  };

  const when = item.created_at ? new Date(item.created_at).toLocaleString('pt-BR') : '';

  return (
    <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: `1px solid ${theme.palette.divider}` }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
        {ehCurtida
          ? <ThumbUpAltOutlined data-testid="icone-curtida" sx={{ fontSize: 18, color: theme.palette.success.main }} />
          : <ThumbDownAltOutlined data-testid="icone-reclamacao" sx={{ fontSize: 18, color: theme.palette.error.main }} />}
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{item.user?.name || 'Operador'}</Typography>
        <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>{when}</Typography>
        <Box sx={{ flex: 1 }} />
        <Chip
          size="small"
          label={isPending ? 'Pendente' : (foiRecusada ? 'Recusada' : 'Revisado')}
          color={isPending ? 'warning' : (foiRecusada ? 'error' : 'success')}
          variant={isPending || foiRecusada ? 'filled' : 'outlined'}
        />
      </Box>

      {item.justification && (
        <Box sx={{ p: 1.25, borderRadius: 2, bgcolor: theme.palette.action.hover, mb: 1 }}>
          <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>{item.justification}</Typography>
        </Box>
      )}

      <Button
        size="small"
        onClick={() => setOpen((v) => !v)}
        endIcon={open ? <ExpandLess /> : <ExpandMore />}
        sx={{ textTransform: 'none', color: theme.palette.text.secondary }}
      >
        {open ? 'Ocultar' : 'Ver pergunta e resposta'}
      </Button>
      <Collapse in={open}>
        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box>
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 700 }}>Pergunta</Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{item.question || '—'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 700 }}>Resposta do Orby</Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{item.response || '—'}</Typography>
          </Box>
        </Box>
      </Collapse>

      {precisaDeCorrecao ? (
        <Box sx={{ mt: 1.5, display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <TextField
            size="small"
            sx={{ flex: '1 1 220px' }}
            placeholder="Nota da revisão (opcional): o que foi feito / ajustado..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
          <Button
            variant="outlined"
            onClick={openCorrection}
            startIcon={<LibraryAddOutlined />}
            sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
          >
            Corrigir na Base
          </Button>

          {/*
            Sair da fila sem corrigir nada -- nos DOIS lados, desde 19/10/2026.

            - Na CURTIDA é "Recusar": o revisor diz que a resposta não era boa,
              e o caso fica como correção pendente.
            - Na RECLAMAÇÃO é "Descartar": o revisor diz que não há o que
              corrigir, e o caso sai da fila. Era o que faltava (pedido da
              Gisele): a reclamação só saía dos pendentes sendo confirmada,
              então uma reclamação improcedente ficava ali para sempre.

            Em item já recusado não aparece: ele já está nesse estado, e o que
            falta é corrigir.
          */}
          {isPending && (
            <Button
              variant="outlined"
              color="error"
              data-testid="botao-recusar"
              onClick={() => review('rejected')}
              disabled={saving}
              startIcon={<HighlightOffOutlined />}
              sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
            >
              {ehCurtida ? 'Recusar' : 'Descartar'}
            </Button>
          )}

          <Button
            variant="contained"
            data-testid="botao-confirmar"
            onClick={() => review(ehCurtida && isPending ? 'confirmed' : undefined)}
            disabled={saving}
            startIcon={<CheckCircleOutline />}
            sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
          >
            {ehCurtida && isPending ? 'Confirmar' : 'Revisado'}
          </Button>
        </Box>
      ) : item.review_notes ? (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
            Revisado por {item.reviewer?.name || '—'}: {item.review_notes}
          </Typography>
        </Box>
      ) : null}

      <Dialog open={correcting} onClose={() => !savingDoc && setCorrecting(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Corrigir na base de conhecimento</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 1.5 }}>
            Escreva a resposta correta. Ela vira um documento na base e passa a orientar o Orby nas próximas perguntas sobre o tema.
          </Typography>
          {item.justification && (
            <Box sx={{ p: 1.25, borderRadius: 2, bgcolor: theme.palette.action.hover, mb: 1.5 }}>
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 700, display: 'block' }}>
                Justificativa do operador
              </Typography>
              <Typography variant="body2">{item.justification}</Typography>
            </Box>
          )}
          <TextField
            label="Título"
            fullWidth
            size="small"
            value={docTitle}
            onChange={(e) => setDocTitle(e.target.value)}
            sx={{ mb: 1.5 }}
          />
          <TextField
            label="Conteúdo (resposta correta)"
            fullWidth
            multiline
            minRows={6}
            value={docContent}
            onChange={(e) => setDocContent(e.target.value)}
            placeholder="Descreva o procedimento correto, passos, links de materiais, etc."
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCorrecting(false)} disabled={savingDoc} sx={{ textTransform: 'none' }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={saveCorrection}
            disabled={savingDoc}
            startIcon={savingDoc ? <CircularProgress size={16} color="inherit" /> : <LibraryAddOutlined />}
            sx={{ textTransform: 'none' }}
          >
            Salvar na base
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default function FeedbackAnalytics() {
  const theme = useTheme();
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState('pending');
  // Abre nas RECLAMAÇÕES, que é o que já existia e o que urge. As curtidas
  // ficam na aba ao lado, sem competir por atenção.
  const [rating, setRating] = useState('dislike');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, list] = await Promise.all([
        http.get('/ai-feedback/stats'),
        http.get('/ai-feedback', { params: { rating, status, per_page: 50 } }),
      ]);
      setStats(s.data);
      setItems(list.data?.data || []);
    } catch (e) {
      // silencioso
    } finally {
      setLoading(false);
    }
  }, [rating, status]);

  useEffect(() => { load(); }, [load]);

  const onReviewed = (id, outcome, eraCurtida) => {
    /*
     * Recusar a CURTIDA não tira o item dos pendentes: ele passa a ser caso
     * de correção e continua precisando de trabalho.
     *
     * Descartar a RECLAMAÇÃO tira: ali o recusado quer dizer "não há o que
     * corrigir", e manter na fila seria pedir trabalho que já se decidiu não
     * fazer. Mesma coluna no banco, significados diferentes -- o que muda é o
     * lado em que o item está.
     */
    const virouRecusada = outcome === 'rejected';
    const ficaNaFila = virouRecusada && eraCurtida;

    setItems((prev) => {
      if (ficaNaFila) {
        return prev.map((i) => (i.id === id ? { ...i, status: 'rejected' } : i));
      }
      return status === 'pending'
        ? prev.filter((i) => i.id !== id)
        : prev.map((i) => (i.id === id ? { ...i, status: virouRecusada ? 'rejected' : 'reviewed' } : i));
    });

    setStats((prev) => {
      if (!prev) return prev;
      const campo = rating === 'like' ? 'likes_pending' : 'pending';
      const atualizado = { ...prev, [campo]: Math.max(0, (prev[campo] || 0) - 1) };
      if (ficaNaFila) {
        atualizado.likes_rejected = (prev.likes_rejected || 0) + 1;
      }
      return atualizado;
    });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>Análises do Orby</Typography>
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
          Feedback dos operadores nas respostas. Os "não curtiu" trazem uma justificativa para você avaliar e melhorar a base.
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
        <StatCard label="SATISFAÇÃO" value={stats?.satisfaction != null ? `${stats.satisfaction}%` : '—'} accent={theme.palette.primary.main} icon={<InsightsOutlined sx={{ fontSize: 18 }} />} />
        <StatCard label="CURTIDAS" value={stats?.likes ?? '—'} accent={theme.palette.success.main} icon={<ThumbUpAltOutlined sx={{ fontSize: 18 }} />} />
        <StatCard label="NÃO CURTIDAS" value={stats?.dislikes ?? '—'} accent={theme.palette.error.main} icon={<ThumbDownAltOutlined sx={{ fontSize: 18 }} />} />
        {/*
          Dois contadores SEPARADOS, nao um so. Juntar faria as reclamacoes
          sumirem no meio das curtidas, que tendem a ser muito mais
          numerosas -- e um numero que mistura "urgente" com "quando der"
          para de servir para decidir o que fazer primeiro.
        */}
        <StatCard label="RECLAMAÇÕES PENDENTES" value={stats?.pending ?? '—'} accent={theme.palette.warning.main} icon={<CheckCircleOutline sx={{ fontSize: 18 }} />} />
        <StatCard label="CURTIDAS A CONFERIR" value={stats?.likes_pending ?? '—'} accent={theme.palette.info.main} icon={<ThumbUpAltOutlined sx={{ fontSize: 18 }} />} />
      </Box>

      <Divider />

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={rating}
          onChange={(_, v) => {
            if (!v) return;
            setRating(v);
            // "Recusadas" só existe para curtida. Voltando para reclamações
            // com esse filtro preso, a lista viria sempre vazia e o operador
            // concluiria que não há reclamação nenhuma.
            if (v === 'dislike' && status === 'rejected') setStatus('pending');
          }}
        >
          <ToggleButton value="dislike" sx={{ textTransform: 'none' }}>Reclamações</ToggleButton>
          <ToggleButton value="like" sx={{ textTransform: 'none' }}>Curtidas</ToggleButton>
        </ToggleButtonGroup>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={status}
          onChange={(_, v) => v && setStatus(v)}
        >
          <ToggleButton value="pending" sx={{ textTransform: 'none' }}>Pendentes</ToggleButton>
          {rating === 'like' && (
            <ToggleButton value="rejected" sx={{ textTransform: 'none' }}>Recusadas</ToggleButton>
          )}
          <ToggleButton value="reviewed" sx={{ textTransform: 'none' }}>Revisados</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={26} /></Box>
      ) : items.length === 0 ? (
        <Paper elevation={0} sx={{ p: 4, borderRadius: 3, border: `1px dashed ${theme.palette.divider}`, textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
            {status === 'pending'
              ? 'Nenhum feedback pendente. Tudo em dia.'
              : status === 'rejected'
                ? 'Nenhuma curtida recusada.'
                : 'Nenhum feedback revisado ainda.'}
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {items.map((item) => (
            <FeedbackItem key={item.id} item={item} onReviewed={onReviewed} />
          ))}
        </Box>
      )}
    </Box>
  );
}
